// frontend/src/features/meetings/components/PublicBookingView.js
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const AVAILABLE_SLOTS = ['09:30 AM', '10:00 AM', '11:30 AM', '02:00 PM', '03:00 PM', '04:30 PM'];

export default function PublicBookingView({
  slug: propSlug,
  meetingTypes: propMeetingTypes = [],
  hosts: propHosts = [],
  isModal = false,
  onClose,
}) {
  const { slug: urlSlug } = useParams();
  const navigate = useNavigate();

  const effectiveSlug = propSlug || urlSlug || 'product-demo';
  const meetingType = propMeetingTypes.find((t) => t.slug === effectiveSlug) ||
    propMeetingTypes[0] || {
      id: 'default',
      slug: effectiveSlug,
      title: 'Schedule a Meeting',
      description: 'Select a convenient time for a meeting.',
      durationMinutes: 30,
    };
  const host = propHosts[0] || {
    id: 'usr-host',
    name: 'Workspace Host',
    email: 'host@lead-huntrix.com',
    role: 'Meeting Host',
    avatar: 'WH',
  };

  const [copiedLink, setCopiedLink] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d;
  });
  const [selectedSlot, setSelectedSlot] = useState(AVAILABLE_SLOTS[1]);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestCompany, setGuestCompany] = useState('');
  const [guestNotes, setGuestNotes] = useState('');
  const [isBooked, setIsBooked] = useState(false);

  // Generate 7 upcoming weekdays
  const upcomingDays = [];
  const curr = new Date();
  let added = 0;
  while (added < 7) {
    curr.setDate(curr.getDate() + 1);
    if (curr.getDay() !== 0 && curr.getDay() !== 6) {
      upcomingDays.push(new Date(curr));
      added++;
    }
  }

  const handleBookingSubmit = (e) => {
    e.preventDefault();
    if (!guestName.trim() || !guestEmail.trim()) return;
    setIsBooked(true);
  };

  const handleCopyLink = () => {
    const publicUrl = `${window.location.origin}/book/${effectiveSlug}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(publicUrl);
    }
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const bookingCardContent = (
    <div className={`public-booking-container ${isModal ? 'is-modal-container' : ''}`}>
      {/* Top Brand / Nav Bar */}
      <div className="public-booking-brand">
        <div className="brand-logo-wrap">
          <img src="/image/Logo.png" alt="Lead Huntrix" className="booking-brand-logo" />
        </div>

        <div className="booking-top-actions">
          {isModal ? (
            <>
              <button
                type="button"
                className="booking-preview-action-btn"
                onClick={handleCopyLink}
                title="Copy public booking URL"
              >
                <i className={`fas ${copiedLink ? 'fa-check text-green' : 'fa-link'}`}></i>
                <span>{copiedLink ? 'Link Copied!' : 'Copy Link'}</span>
              </button>
              <a
                href={`/book/${effectiveSlug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="booking-preview-action-btn"
                title="Open in new browser tab"
              >
                <i className="fas fa-external-link-alt"></i>
                <span>Open Tab</span>
              </a>
              <button
                type="button"
                className="booking-preview-close-btn"
                onClick={onClose}
                title="Close preview"
              >
                <i className="fas fa-times"></i>
              </button>
            </>
          ) : (
            <button
              type="button"
              className="booking-back-link"
              onClick={() => navigate('/meetings')}
            >
              <i className="fas fa-arrow-left"></i> Back to Dashboard
            </button>
          )}
        </div>
      </div>

      {isBooked ? (
        /* Confirmation Screen */
        <div className="booking-confirmed-card">
          <div className="confirmed-icon-circle">
            <i className="fas fa-check"></i>
          </div>
          <h2>Meeting Confirmed!</h2>
          <p className="confirmed-subtitle">
            A calendar invitation with conferencing details has been dispatched to{' '}
            <strong>{guestEmail}</strong>.
          </p>

          <div className="confirmed-details-box">
            <div className="confirmed-detail-row">
              <i className="far fa-calendar-check"></i>
              <div>
                <strong>{meetingType.title}</strong>
                <span>
                  {selectedDate.toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              </div>
            </div>

            <div className="confirmed-detail-row">
              <i className="far fa-clock"></i>
              <div>
                <strong>{selectedSlot}</strong>
                <span>Timezone: Asia/Singapore (SGT UTC+8)</span>
              </div>
            </div>

            <div className="confirmed-detail-row">
              <i className="fas fa-video"></i>
              <div>
                <strong>Google Meet</strong>
                <span className="conf-url-text">https://meet.google.com/sched-demo-meet</span>
              </div>
            </div>

            <div className="confirmed-detail-row">
              <i className="fas fa-user-tie"></i>
              <div>
                <strong>Host: {host.name}</strong>
                <span>{host.role} · Lead Huntrix</span>
              </div>
            </div>
          </div>

          <div className="confirmed-actions">
            <button
              type="button"
              className="meetings-btn meetings-btn-primary"
              onClick={() => {
                alert('Calendar event (.ics) downloaded.');
              }}
            >
              <i className="fas fa-calendar-plus"></i> Add to Calendar (.ics)
            </button>
            <button
              type="button"
              className="meetings-btn meetings-btn-outline"
              onClick={() => setIsBooked(false)}
            >
              Schedule Another Meeting
            </button>
          </div>
        </div>
      ) : (
        /* Main Booking Grid */
        <div className="public-booking-grid">
          {/* Left Pane: Host & Details */}
          <div className="booking-left-pane">
            <div className="host-profile-card">
              <div className="host-avatar-large">{host.avatar}</div>
              <div className="host-profile-text">
                <h3 className="host-name">{host.name}</h3>
                <span className="host-title">{host.role}</span>
              </div>
            </div>

            <h1 className="booking-meeting-title">{meetingType.title}</h1>

            <div className="booking-meta-list">
              <div className="booking-meta-item">
                <i className="far fa-clock"></i>
                <span>{meetingType.durationMinutes || 30} minutes</span>
              </div>
              <div className="booking-meta-item">
                <i className="fas fa-video"></i>
                <span>Web conferencing link provided upon confirmation</span>
              </div>
              <div className="booking-meta-item">
                <i className="fas fa-globe-asia"></i>
                <span>Asia/Singapore (UTC+8)</span>
              </div>
              <div className="booking-meta-item">
                <i className="fas fa-calendar-check" style={{ color: '#059669' }}></i>
                <span>Real-Time Calendar Sync (Google & Microsoft 365)</span>
              </div>
            </div>

            <p className="booking-description">{meetingType.description}</p>
          </div>

          {/* Right Pane: Date, Slot & Guest Form */}
          <div className="booking-right-pane">
            <h2 className="step-title">1. Select a Date & Time</h2>

            {/* Day selection pills */}
            <div className="date-pills-row">
              {upcomingDays.map((d, i) => {
                const isSelected = d.toDateString() === selectedDate.toDateString();
                return (
                  <button
                    key={i}
                    type="button"
                    className={`date-pill-btn ${isSelected ? 'active' : ''}`}
                    onClick={() => setSelectedDate(d)}
                  >
                    <span className="pill-day">
                      {d.toLocaleDateString('en-US', { weekday: 'short' })}
                    </span>
                    <span className="pill-num">{d.getDate()}</span>
                    <span className="pill-month">
                      {d.toLocaleDateString('en-US', { month: 'short' })}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Time Slots Grid */}
            <div className="slots-section">
              <span className="slots-heading">Available Slots:</span>
              <div className="slots-grid">
                {AVAILABLE_SLOTS.map((slot) => (
                  <button
                    key={slot}
                    type="button"
                    className={`slot-chip ${selectedSlot === slot ? 'active' : ''}`}
                    onClick={() => setSelectedSlot(slot)}
                  >
                    {slot}
                  </button>
                ))}
              </div>
            </div>

            <h2 className="step-title mt-6">2. Your Contact Information</h2>

            <form onSubmit={handleBookingSubmit} className="booking-form">
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input
                  type="text"
                  required
                  placeholder="E.g. Jennifer Tan"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Work Email *</label>
                <input
                  type="email"
                  required
                  placeholder="E.g. jennifer@company.com"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Company Name</label>
                <input
                  type="text"
                  placeholder="E.g. Nexus Technology"
                  value={guestCompany}
                  onChange={(e) => setGuestCompany(e.target.value)}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Topics / What would you like to cover?</label>
                <textarea
                  rows={2}
                  placeholder="Share any specific topics or goals..."
                  value={guestNotes}
                  onChange={(e) => setGuestNotes(e.target.value)}
                  className="form-textarea"
                />
              </div>

              <button
                type="submit"
                className="meetings-btn meetings-btn-primary full-width"
                disabled={!guestName.trim() || !guestEmail.trim()}
              >
                <i className="fas fa-calendar-check"></i>
                <span>Confirm Booking ({selectedSlot})</span>
              </button>
            </form>
          </div>
        </div>
      )}

      <div className="public-booking-footer">
        <span>
          Powered by <strong>Lead Huntrix</strong> · Autonomous Outbound & Meeting Intelligence
        </span>
      </div>
    </div>
  );

  if (isModal) {
    return (
      <div className="meetings-modal-overlay" onClick={onClose}>
        <div className="booking-modal-wrapper" onClick={(e) => e.stopPropagation()}>
          {bookingCardContent}
        </div>
      </div>
    );
  }

  return <div className="public-booking-page">{bookingCardContent}</div>;
}
